import {
  EShowArchType,
  stageControl,
  stagingManager,
  wasmModule,
} from "@/gluelayer"
import { spaceManagement } from "@/gluelayer/core/modules/spaceManagement"
import MyDialog from "@/shared/components/MyDialog"
import { IPRDistributionList } from "@/shared/data/ud-treat"
import { EIPRNtoNType } from "@/shared/types"
import utils from "@/shared/utils"
import { Button, Tooltip } from "@mui/material"
import { FC, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"

const { setStageCallback, setStageStep, getWasmStageData } = stageControl

interface IProps {
  curArch: string
  setPageLoading: (bool: boolean) => void
}
const IPRSpaceManagement: FC<IProps> = ({ curArch, setPageLoading }) => {
  const dispatch = useDispatch()
  const [type, setType] = useState<EIPRNtoNType>()
  const [isShowMidlineDialog, setIsShowMidlineDialog] = useState(false)
  const [isShowFinalStageModel, setIsShowFinalStageModel] = useState(false)
  const [isAutoCorrectionMidlineModel, setIsAutoCorrectionMidlineModel] =
    useState(false)
  const [isPassiveAligner, setIsPassiveAligner] = useState(false)
  const [data, setData] = useState<{
    UpData: string
    LowData: string
  }>({
    UpData: "0",
    LowData: "0",
  })

  const toothType = useMemo(() => {
    const type =
      curArch === "up"
        ? EShowArchType.UpArch
        : curArch === "low"
        ? EShowArchType.LowArch
        : EShowArchType.BothArch
    return type
  }, [curArch])

  /**
   *  verify passive aligner Dialog
   */
  const verifyPassiveAligner = () => {
    const containsPA = stagingManager.isStageContainsPA(toothType)
    if (containsPA) {
      setIsPassiveAligner(true)
      return true
    } else {
      return false
    }
  }

  /**
   *  verify Final Stage Dialog
   */
  const verifyIsFinalStage = () => {
    const isFinalStage = spaceManagement.IsFinalStage(toothType)
    if (!isFinalStage) {
      setIsShowFinalStageModel(true)
      return true
    } else {
      return false
    }
  }

  /**
   * Set Final stage
   */
  const setFinalStage = () => {
    const stageController = wasmModule.ulabwinIns.getStageContoller()
    if (stageController) {
      for (let i = 0; i < 2; i++) {
        stageController.AutoStage(i)
      }
      stageController.FinalStage(0)
      stageController.FinalStage(1)
    }
  }

  /**
   * IPR Distribution
   */
  const selectIPRDistribution = (isMidLine: boolean) => {
    const isOpen = spaceManagement.GetModuleSpaceManagement()
    if (isOpen) {
      spaceManagement.SetIPRNtoN(type, isMidLine)
    } else {
      spaceManagement.SwitchModuleSpaceManagement(true)
      spaceManagement.SetIPRNtoN(type, isMidLine)
    }
    setIsShowMidlineDialog(false)
  }

  /**
   * Space Management
   */
  const handleSpaceManagement = async (bool: boolean) => {
    setIsAutoCorrectionMidlineModel(false)
    if (!bool) return
    setPageLoading(true)
    await utils.sleep(200)
    // UpArch = 0,LowArch = 1, BothArch = 2,
    // up, low, all
    spaceManagement.SetResolveCollision(toothType, () => {
      setPageLoading(false)
    })
  }

  /**
   * Auto Correction Midline Model
   */
  const handleAutoCorrectionMidline = () => {
    const isPassiveAligner = verifyPassiveAligner()
    if (isPassiveAligner) return
    const isFinalStage = verifyIsFinalStage()
    if (isFinalStage) return
    const isMidLine = spaceManagement.IsWillShift(toothType)
    if (isMidLine) {
      setIsAutoCorrectionMidlineModel(true)
    } else {
      handleSpaceManagement(true)
    }
  }

  /**
   * Init Data
   */
  const getTotalIPRValue = () => {
    const iprValue = spaceManagement.GetTotalIPRValue()
    if (iprValue) {
      if (!iprValue.size()) return
      const arr = [data.UpData, data.LowData]
      for (let i = 0; i < iprValue.size(); i++) {
        arr[i] = iprValue.get(i)
      }
      setData({
        UpData: arr[0],
        LowData: arr[1],
      })
    }
  }

  useEffect(() => {
    const isOpen = spaceManagement.GetModuleSpaceManagement()
    if (!isOpen) {
      spaceManagement.SwitchModuleSpaceManagement(true)
    }
    getTotalIPRValue()
    spaceManagement.SetUpdateTotalIPRCallback(
      (UpData: string, LowData: string) => {
        setData({ UpData, LowData })
      },
    )
  }, [])

  return (
    <div className="card-item-body">
      <div className="total-ipr-value">
        <div className="title"> Total IPR Value: </div>
        <div className="value">
          <div>
            <span>U</span>: {data.UpData || 0}
          </div>
          <div>
            <span>L</span>: {data.LowData || 0}
          </div>
        </div>
      </div>
      <h3 className="card-item-sub-title">IPR Distribution</h3>
      <div className="content-space mb-15">
        {IPRDistributionList.map((item) => (
          <Button
            key={item.value}
            className="btn w-100 btn-secondary"
            variant="contained"
            onClick={() => {
              const isPassiveAligner = verifyPassiveAligner()
              if (isPassiveAligner) return
              const isFinalStage = verifyIsFinalStage()
              if (isFinalStage) return
              setType(item.value as EIPRNtoNType)
              setIsShowMidlineDialog(true)
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>
      <h3 className="card-item-sub-title">Space Mgmt.</h3>
      <Tooltip title="Resolve 0.5mm space/collision per click" placement="top">
        <Button
          className="btn w-100 btn-secondary"
          variant="contained"
          onClick={handleAutoCorrectionMidline}
        >
          Auto Correction
        </Button>
      </Tooltip>

      <MyDialog
        open={isAutoCorrectionMidlineModel}
        title="Warning"
        content="This adjustment could change your midline slightly. Would you like to proceed? "
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-primary"
              variant="contained"
              onClick={() => handleSpaceManagement(true)}
            >
              Yes
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => handleSpaceManagement(false)}
            >
              No
            </Button>
          </div>
        }
      />

      {/*midline*/}
      <MyDialog
        open={isShowMidlineDialog}
        title="Warning"
        content="Do you want to maintain midline?"
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-primary"
              variant="contained"
              onClick={() => selectIPRDistribution(true)}
            >
              Yes
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => selectIPRDistribution(false)}
            >
              No
            </Button>
          </div>
        }
      />

      {/*finial stage*/}
      <MyDialog
        open={isShowFinalStageModel}
        title="Warning"
        content="This function only works on the finial stage."
        cancelText="OK"
        onClose={() => {
          setFinalStage()
          setIsShowFinalStageModel(false)
        }}
      />

      {/*Changes to the tooth movement plan will require new staging of passive aligner.*/}
      <MyDialog
        open={isPassiveAligner}
        title="Warning"
        content="Changes to the tooth movement plan will require new staging of passive aligner."
        actions={
          <div className="d-flex gap-1">
            <Button
              className="btn btn-primary"
              variant="contained"
              onClick={() => {
                stagingManager.delPassiveAligner(toothType)
                setIsPassiveAligner(false)
              }}
            >
              Confirm
            </Button>
            <Button
              className="btn btn-outline-primary"
              variant="outlined"
              onClick={() => setIsPassiveAligner(false)}
            >
              Cancel
            </Button>
          </div>
        }
      />
    </div>
  )
}

export default IPRSpaceManagement
